import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { translations, LANGUAGES } from "@/i18n/translations";
import { api } from "@/lib/http";

const I18nContext = createContext(null);

const LS_KEY = "solano_lang";
const LS_SYNCED_LANG = "solano_lang_synced_at";

function detectDefault() {
  try {
    const stored = localStorage.getItem(LS_KEY);
    if (stored && translations[stored]) return stored;
    const nav = (navigator.language || "it").slice(0, 2).toLowerCase();
    if (translations[nav]) return nav;
  } catch {}
  return "it";
}

async function syncLangToBackend(lang) {
  // Best-effort: silently sync the UI language into the user's push
  // notification prefs so scheduled pushes land in the language they read.
  // Ignores 401 (guest visitor) and network errors — user experience unaffected.
  try {
    await api.put("/client/notification-prefs/lang", { lang });
    localStorage.setItem(LS_SYNCED_LANG, lang);
  } catch {
    // ignore — will retry on next language switch or reload
  }
}

// Swiss German has no "ß" (Paprikasauce → "Paprikasosse", not "Paprikasoße").
// Region: Europe/Zurich timezone or a de-CH / *-CH browser locale → "ss".
// DE/AT keep "ß". Applied to UI strings in t() and, via a DOM observer, to
// dynamic content (AI recipe names, descriptions) so the whole app is consistent.
export function isSwissRegion() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz === "Europe/Zurich" || tz === "Europe/Vaduz") return true;
    return (navigator.languages || [navigator.language]).some((l) => /-(CH|LI)$/i.test(l || ""));
  } catch { return false; }
}
export const deCH = (s) => (typeof s === "string" ? s.replace(/ß/g, "ss") : s);

function useSwissSpelling(enabled) {
  useEffect(() => {
    if (!enabled) return;
    const fix = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        if (node.nodeValue && node.nodeValue.includes("ß")) node.nodeValue = node.nodeValue.replace(/ß/g, "ss");
        return;
      }
      if (node.nodeType !== Node.ELEMENT_NODE || /^(SCRIPT|STYLE|TEXTAREA|INPUT)$/.test(node.tagName)) return;
      const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
      let n; while ((n = walker.nextNode())) { if (n.nodeValue && n.nodeValue.includes("ß")) n.nodeValue = n.nodeValue.replace(/ß/g, "ss"); }
    };
    fix(document.body);
    const obs = new MutationObserver((muts) => {
      for (const m of muts) {
        if (m.type === "characterData") fix(m.target);
        else m.addedNodes.forEach(fix);
      }
    });
    obs.observe(document.body, { childList: true, subtree: true, characterData: true });
    return () => obs.disconnect();
  }, [enabled]);
}

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(detectDefault);
  const lastSyncedRef = useRef(null);
  const swiss = lang === "de" && isSwissRegion();
  useSwissSpelling(swiss);

  useEffect(() => {
    try { localStorage.setItem(LS_KEY, lang); } catch {}
    document.documentElement.lang = lang;
    // Push language to the backend so scheduled push notifications match.
    // Only sync when the language actually changes for this session.
    if (lastSyncedRef.current !== lang) {
      lastSyncedRef.current = lang;
      syncLangToBackend(lang);
    }
  }, [lang]);

  const setLang = (l) => {
    if (translations[l]) setLangState(l);
  };

  const t = useCallback((path, params) => {
    const parts = path.split(".");
    // Try lang → fallback to English → fallback to Italian
    const resolve = (dict) => {
      let node = dict;
      for (const p of parts) {
        if (node && typeof node === "object" && p in node) node = node[p];
        else return undefined;
      }
      // Allow both strings and arrays (e.g. list of foods for a plate slice)
      return typeof node === "string" || Array.isArray(node) ? node : undefined;
    };
    let value = resolve(translations[lang]);
    if (value === undefined) value = resolve(translations.en);
    if (value === undefined) value = resolve(translations.it);
    if (value === undefined) return path;
    if (params && typeof params === "object" && typeof value === "string") {
      value = value.replace(/\{(\w+)\}/g, (_, key) => (params[key] !== undefined ? String(params[key]) : `{${key}}`));
    }
    return swiss ? deCH(value) : value;
  }, [lang, swiss]);

  return (
    <I18nContext.Provider value={{ lang, setLang, t, languages: LANGUAGES }}>
      {children}
    </I18nContext.Provider>
  );
}

export const useT = () => useContext(I18nContext);
